import { createContext, useContext, useEffect, useState } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Layout } from "./components/Layout";
import { SnapshotProvider } from "./lib/snapshot";
import {
  cognitoConfigured,
  isAdmin,
  onAuthChange,
  restoreSession,
} from "./lib/auth";
import { Overview } from "./screens/Overview";
import { Dashboard } from "./screens/Dashboard";
import { ServiceDetail } from "./screens/ServiceDetail";
import { Incidents } from "./screens/Incidents";
import { ServicesAdmin } from "./screens/ServicesAdmin";
import { AddService } from "./screens/AddService";
import { WebhookInspector } from "./screens/WebhookInspector";
import { Settings } from "./screens/Settings";
import { SignIn } from "./screens/SignIn";
import { MagicLink } from "./screens/MagicLink";
import { OAuthCallback } from "./screens/OAuthCallback";

const AuthRestoreContext = createContext<boolean>(false);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30_000 },
  },
});

export function App() {
  const [, force] = useState(0);
  const [restoring, setRestoring] = useState(cognitoConfigured);

  useEffect(() => {
    const unsub = onAuthChange(() => force((n) => n + 1));
    if (cognitoConfigured) {
      void restoreSession().finally(() => setRestoring(false));
    }
    return unsub;
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthRestoreContext.Provider value={restoring}>
        <BrowserRouter>
          <SnapshotProvider>
            <Layout>
              <Routes>
                <Route path="/" element={<Overview />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/services/:serviceId" element={<ServiceDetail />} />
                <Route path="/incidents" element={<Incidents />} />
                <Route path="/incidents/:incidentId" element={<Incidents />} />
                <Route path="/signin" element={<SignInRoute />} />
                <Route path="/auth/magic" element={<MagicLink />} />
                <Route path="/auth/callback" element={<OAuthCallback />} />
                <Route
                  path="/admin/services"
                  element={
                    <AdminGuard>
                      <ServicesAdmin />
                    </AdminGuard>
                  }
                />
                <Route
                  path="/admin/add"
                  element={
                    <AdminGuard>
                      <AddService />
                    </AdminGuard>
                  }
                />
                <Route
                  path="/admin/webhooks"
                  element={
                    <AdminGuard>
                      <WebhookInspector />
                    </AdminGuard>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <AdminGuard>
                      <Settings />
                    </AdminGuard>
                  }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Layout>
          </SnapshotProvider>
        </BrowserRouter>
      </AuthRestoreContext.Provider>
    </QueryClientProvider>
  );
}

function AdminGuard({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const restoring = useContext(AuthRestoreContext);
  if (restoring) return null;
  if (!isAdmin()) {
    const returnTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/signin?returnTo=${returnTo}`} replace />;
  }
  return <>{children}</>;
}

function SignInRoute() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnTo = params.get("returnTo") || "/";
  return <SignIn onSignedIn={() => navigate(returnTo, { replace: true })} />;
}
